
import React from "react";
import { UseFormReturn } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { CreditCard, Check } from "lucide-react";
import { FormValues } from "./types";
import { useTranslation } from "react-i18next";

interface GiftCardSectionProps {
  form: UseFormReturn<FormValues>;
  useGiftCard: boolean;
  isVerifyingCard: boolean;
  giftCardVerified: boolean;
  onVerifyGiftCard: (code: string) => Promise<void>;
  onResetVerification: () => void;
}

export const GiftCardSection: React.FC<GiftCardSectionProps> = ({ 
  form,
  useGiftCard,
  isVerifyingCard,
  giftCardVerified,
  onVerifyGiftCard,
  onResetVerification
}) => {
  const { t } = useTranslation();
  const giftCardCode = form.watch("giftCardCode");
  
  console.log("GiftCardSection render:", { 
    useGiftCard, 
    giftCardCode, 
    giftCardVerified, 
    isVerifyingCard 
  });
  
  // Track the verified code to prevent unnecessary resets
  const [verifiedCode, setVerifiedCode] = React.useState<string>("");
  
  // Update verifiedCode when verification succeeds
  React.useEffect(() => {
    if (giftCardVerified && giftCardCode) {
      setVerifiedCode(giftCardCode);
    }
  }, [giftCardVerified, giftCardCode]);
  
  // Reset verification only when code changes after being verified
  React.useEffect(() => {
    if (giftCardVerified && giftCardCode !== verifiedCode && verifiedCode !== "") {
      onResetVerification();
    }
  }, [giftCardCode, giftCardVerified, onResetVerification, verifiedCode]);
  
  const handleVerifyClick = () => {
    if (giftCardCode) {
      onVerifyGiftCard(giftCardCode);
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-3 border border-border rounded-md">
        <div className="flex items-center gap-2">
          <CreditCard size={18} />
          <div className="space-y-1 leading-none">
            <span className="font-medium">{t('booking.form.useGiftCard')}</span>
            <p className="text-xs text-muted-foreground">
              {t('booking.giftCard.subtitle')}
            </p>
          </div>
        </div>
        <FormField
          control={form.control}
          name="useGiftCard"
          render={({ field }) => (
            <FormControl>
              <Switch
                checked={field.value}
                onCheckedChange={(value) => {
                  field.onChange(value);
                  if (!value) {
                    onResetVerification();
                  }
                  console.log("Gift card switch changed to:", value);
                }}
              />
            </FormControl>
          )}
        />
      </div>

      {useGiftCard && (
        <div className="space-y-4 pt-2 px-3">
          <FormField
            control={form.control}
            name="giftCardCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('booking.giftCard.code')}</FormLabel>
                <div className="flex space-x-2">
                  <FormControl>
                    <Input 
                      placeholder={t('booking.giftCard.enterCode')} 
                      {...field} 
                      className="uppercase"
                      onChange={(e) => {
                        const newValue = e.target.value.toUpperCase();
                        field.onChange(newValue);
                        if (giftCardVerified && newValue !== field.value) {
                          onResetVerification();
                        }
                      }}
                    />
                  </FormControl>
                  <Button 
                    type="button" 
                    size="sm" 
                    onClick={handleVerifyClick}
                    disabled={isVerifyingCard || !giftCardCode || giftCardVerified}
                    variant={giftCardVerified ? "outline" : "default"}
                  >
                    {isVerifyingCard ? t('booking.giftCard.verifying') : 
                      giftCardVerified ? (
                        <span className="flex items-center">
                          {t('booking.giftCard.verified')} <Check className="ml-1 h-4 w-4" />
                        </span>
                      ) : t('booking.giftCard.verify')}
                  </Button>
                </div>
                <FormMessage />
                {giftCardVerified && (
                  <p className="text-sm text-green-600 mt-1 flex items-center">
                    <Check className="h-4 w-4 mr-1" /> {t('booking.giftCard.successMessage')}
                  </p>
                )}
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  );
};
